import { ref } from 'vue'
import { defineStore } from 'pinia'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: string
  message: string
  type: ToastType
  duration: number
  createdAt: number
}

export type BannerType = 'infra-lock' | 'audit-degraded' | 'build-failed'

export interface Banner {
  type: BannerType
  message: string
  details?: string
}

let toastIdCounter = 0

export const useNotificationsStore = defineStore('notifications', () => {
  const toasts = ref<Toast[]>([])
  const globalBanner = ref<Banner | null>(null)

  function pushToast(message: string, type: ToastType = 'info', duration = 5000): void {
    const id = `toast-${++toastIdCounter}`
    toasts.value.push({ id, message, type, duration, createdAt: Date.now() })
    setTimeout(() => clearToast(id), duration)
  }

  function clearToast(id: string): void {
    const idx = toasts.value.findIndex(t => t.id === id)
    if (idx !== -1) {
      toasts.value.splice(idx, 1)
    }
  }

  function setGlobalBanner(banner: Banner): void {
    globalBanner.value = banner
  }

  function clearGlobalBanner(): void {
    globalBanner.value = null
  }

  function pushSuccess(message: string): void {
    pushToast(message, 'success')
  }

  function pushError(message: string): void {
    pushToast(message, 'error', 8000)
  }

  function pushInfo(message: string): void {
    pushToast(message, 'info')
  }

  return {
    toasts,
    globalBanner,
    pushToast,
    clearToast,
    setGlobalBanner,
    clearGlobalBanner,
    pushSuccess,
    pushError,
    pushInfo,
  }
})
